import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, MapPin } from "lucide-react";
import LangSwitcher from "@/components/LangSwitcher";
import CartButton from "@/components/CartButton";
import { localePath, parseLocaleFromPath } from "@/lib/locale";
import { LOCALE_KEY, SUPPORTED_LOCALES } from "@/i18n";
import { useConsent } from "@/context/ConsentContext";
import useSiteConfig from "@/hooks/useSiteConfig";

const NAV_ITEMS = [
  { key: "home", path: "/" },
  { key: "models", path: "/models" },
  { key: "story", path: "/story" },
  { key: "how_its_made", path: "/how-its-made" },
  { key: "restorations", path: "/restorations" },
  { key: "service", path: "/service" },
  { key: "gallery", path: "/gallery" },
  { key: "contact", path: "/contact" },
];

// Footer nav — excludes Restauracija (removed per client request; the /restorations
// route still works via direct URL and remains in the sitemap).
const FOOTER_NAV_ITEMS = NAV_ITEMS.filter((n) => n.key !== "restorations");

// Footer instrument list — 6 visible products (Mandolina + Berda-ag archived).
const INSTRUMENT_LINKS = [
  { slug: "primovi", label: "Prim (E, D)" },
  { slug: "bisernice", label: "Bisernica (G)" },
  { slug: "bracevi", label: "Brač (A, G)" },
  { slug: "bugarije", label: "Bugarija / Kontra" },
  { slug: "cela", label: "Čelo (A, G)" },
  { slug: "berde", label: "Berda (A, G)" },
];

function BrandMark({ to }) {
  const [logoLoaded, setLogoLoaded] = useState(false);
  return (
    <Link to={to} data-testid="brand-link" className="flex items-center gap-3 group">
      <img
        src="/logo.png"
        alt=""
        aria-hidden="true"
        onLoad={() => setLogoLoaded(true)}
        onError={() => setLogoLoaded(false)}
        className={`h-9 w-auto ${logoLoaded ? "" : "hidden"}`}
      />
      <span data-testid="brand-wordmark" className="font-serif leading-tight">
        <span className="block text-[10px] uppercase tracking-[0.3em] text-muted-foreground -mb-0.5">tambure</span>
        <span className="block text-xl text-foreground tracking-tight">ŽMEGAČ</span>
      </span>
    </Link>
  );
}

export default function PublicLayout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { locale } = parseLocaleFromPath(location.pathname);
  const { reset: openConsent } = useConsent();
  const cfg = useSiteConfig();
  const commerceOn = !!cfg.commerce_mode_enabled;

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
      localStorage.setItem(LOCALE_KEY, locale);
    }
  }, [locale, i18n]);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const homeHref = localePath(locale, "/");

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-6">
          <BrandMark to={homeHref} />
          <nav className="hidden lg:flex items-center gap-7 text-sm" data-testid="primary-nav">
            {NAV_ITEMS.filter((n) => n.key !== "home").map((item) => (
              <NavLink
                key={item.key}
                to={localePath(locale, item.path)}
                end
                data-testid={`nav-${item.key}`}
                className={({ isActive }) =>
                  `transition-colors hover:text-primary ${isActive ? "text-primary" : "text-foreground/75"}`
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden md:block"><LangSwitcher /></div>
            {commerceOn && <CartButton />}
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden rounded-lg border border-border/60 p-2 hover:bg-muted"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-4 w-4" strokeWidth={1.5} /> : <Menu className="h-4 w-4" strokeWidth={1.5} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t border-border/60 bg-background" data-testid="mobile-nav">
            <div className="px-5 py-5 space-y-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.key}
                  to={localePath(locale, item.path)}
                  end
                  data-testid={`mobile-nav-${item.key}`}
                  className={({ isActive }) =>
                    `block px-3 py-2.5 rounded-lg text-base ${isActive ? "bg-primary/10 text-primary" : "text-foreground/85 hover:bg-muted"}`
                  }
                >
                  {t(`nav.${item.key}`)}
                </NavLink>
              ))}
              <div className="pt-3"><LangSwitcher /></div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-border/60 bg-tz-espresso text-tz-cream/85">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="font-serif text-xl leading-tight">
              <span className="block text-[10px] uppercase tracking-[0.3em] text-tz-cream/50 -mb-0.5">tambure</span>
              <span className="block text-tz-cream">ŽMEGAČ</span>
            </div>
            <p className="mt-4 text-sm text-tz-cream/60 leading-relaxed max-w-xs">
              {t("brand.tagline")}
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-tz-cream/50 mb-3">{t("footer.navigate")}</div>
            <ul className="space-y-2 text-sm">
              {FOOTER_NAV_ITEMS.slice(0, 6).map((item) => (
                <li key={item.key}>
                  <Link
                    to={localePath(locale, item.path)}
                    className="hover:text-primary transition-colors"
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-tz-cream/50 mb-3">{t("footer.instruments")}</div>
            <ul className="space-y-2 text-sm">
              {INSTRUMENT_LINKS.map((ins) => (
                <li key={ins.slug}>
                  <Link
                    to={localePath(locale, `/${ins.slug}`)}
                    className="hover:text-primary transition-colors"
                  >
                    {ins.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-tz-cream/50 mb-3">{t("footer.contact")}</div>
            <address className="not-italic text-sm space-y-1 text-tz-cream/80">
              <div className="font-medium text-tz-cream">{t("footer.workshop")} {t("footer.owner")}</div>
              <div className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-1 text-primary" strokeWidth={1.5} />
                <span>{t("footer.address_line1")}<br/>{t("footer.address_line2")}</span>
              </div>
              <div className="pt-2 space-y-1">
                <a href={`tel:${t("footer.phone_landline_tel")}`} className="block hover:text-primary transition-colors">
                  <span className="text-tz-cream/50 text-[10px] uppercase tracking-[0.18em] mr-2">{t("footer.phone_landline_label")}</span>
                  {t("footer.phone_landline")}
                </a>
                <a href={`tel:${t("footer.phone_mobile_tel")}`} className="block hover:text-primary transition-colors">
                  <span className="text-tz-cream/50 text-[10px] uppercase tracking-[0.18em] mr-2">{t("footer.phone_mobile_label")}</span>
                  {t("footer.phone_mobile")}
                </a>
                <a href={`mailto:${t("footer.email")}`} className="block hover:text-primary transition-colors break-all">{t("footer.email")}</a>
              </div>
            </address>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-5 lg:px-10 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-tz-cream/55">
            <div>© {new Date().getFullYear()} Tambure Žmegač. {t("footer.rights")}</div>
            <div className="flex items-center gap-4">
              <Link to={localePath(locale, "/terms")} className="hover:text-primary transition-colors">{t("footer.terms")}</Link>
              <button
                type="button"
                onClick={openConsent}
                data-testid="cookie-preferences-link"
                className="hover:text-primary transition-colors"
              >
                {t("footer.cookies")}
              </button>
              <Link to="/admin/login" className="hover:text-primary transition-colors">{t("nav.admin")}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
